/** @format */

import React from "react";

// Utility to disable React DevTools in production or when causing issues
// This can help prevent the "Cannot set properties of undefined (setting 'toggle')" error

const disableReactDevTools = () => {
  try {
    // Check if we're in development mode
    if (process.env.NODE_ENV !== "development") {
      // Disable React DevTools in production
      if (
        typeof window !== "undefined" &&
        window.__REACT_DEVTOOLS_GLOBAL_HOOK__
      ) {
        window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberRoot = null;
        window.__REACT_DEVTOOLS_GLOBAL_HOOK__.onCommitFiberUnmount = null;
        window.__REACT_DEVTOOLS_GLOBAL_HOOK__.supportsFiber = false;

        // Additional safety measures
        Object.defineProperty(
          window.__REACT_DEVTOOLS_GLOBAL_HOOK__,
          "isDisabled",
          {
            value: true,
            writable: false,
            configurable: false,
          }
        );
      }
    }
  } catch (error) {
    console.warn("Could not disable React DevTools:", error);
  }
};

// Function to suppress React DevTools errors
const suppressReactDevToolsErrors = () => {
  try {
    // Override console.error to filter out React DevTools related errors
    const originalError = console.error;
    console.error = (...args) => {
      const message = args[0]?.toString() || "";

      // Filter out React DevTools and React.jsx related errors
      if (
        message.includes(
          "Cannot set properties of undefined (setting 'toggle')"
        ) ||
        message.includes("ReactToastify.css") ||
        message.includes("__REACT_DEVTOOLS_GLOBAL_HOOK__") ||
        message.includes("React.jsx: type is invalid") ||
        message.includes(
          "expected a string (for built-in components) or a class/function"
        ) ||
        message.includes("but got: object") ||
        message.includes("You likely forgot to export your component") ||
        message.includes("mixed up default and named imports")
      ) {
        // Log a simplified warning instead of the full error
        console.warn(
          "React DevTools warning suppressed:",
          message.substring(0, 100) + "..."
        );
        return;
      }

      // Call original console.error for other errors
      originalError.apply(console, args);
    };
  } catch (error) {
    console.warn("Could not override console.error:", error);
  }
};

// Function to help debug component import issues
const debugComponentImports = () => {
  try {
    // Check for common import issues
    const checkComponent = (componentName, component) => {
      if (component === undefined) {
        console.warn(
          `Component "${componentName}" is undefined - check import path`
        );
      } else if (
        typeof component === "object" &&
        component !== null &&
        !React.isValidElement(component)
      ) {
        console.warn(
          `Component "${componentName}" is an object but not a valid React element - check export/import syntax`
        );
      } else if (typeof component === "function") {
        console.log(
          `Component "${componentName}" imported correctly as function`
        );
      } else {
        console.log(`Component "${componentName}" type:`, typeof component);
      }
    };

    // This function can be called from console to debug specific components
    window.debugComponent = checkComponent;
    console.log(
      "Debug helper available: window.debugComponent(name, component)"
    );
  } catch (error) {
    console.warn("Could not setup component debugging:", error);
  }
};

// Default export with named exports
export {
  disableReactDevTools,
  suppressReactDevToolsErrors,
  debugComponentImports,
};

// Also provide a default export
export default {
  disableReactDevTools,
  suppressReactDevToolsErrors,
  debugComponentImports,
};
